import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import Logo from "@/components/common/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { useBuyer } from "@/contexts/BuyerContext";
import { useBuyerAuth } from "@/hooks/useBuyerAuth";
import { generateInitials } from "@/utils/userUtils";
import { Menu } from "lucide-react";

const BuyerLandingHeader = () => {
  const { user, isLoading: authLoading } = useAuth();
  const { buyerProfile, isBuyer } = useBuyer();
  const { signOutAsBuyer } = useBuyerAuth();

  // Generate user initials from buyer profile name
  const userInitials = generateInitials(buyerProfile?.full_name);

  const handleLogout = () => {
    signOutAsBuyer();
  };

  return (
    <header className="border-b border-gray-800 bg-[#080c14] pt-[env(safe-area-inset-top)]">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <Logo className="text-[#ffd700]" context="buyer" />
        <div className="flex items-center gap-4">
          {authLoading ? (
            // Show loading state while determining auth status
            <div className="flex items-center gap-4">
              <div className="h-10 w-20 bg-gray-700 animate-pulse rounded"></div>
              <div className="h-10 w-24 bg-gray-700 animate-pulse rounded"></div>
            </div>
          ) : user && buyerProfile && isBuyer ? (
            // Show user info and dropdown when logged in as buyer
            <div className="flex items-center gap-4">
              <Link to="/buyer/dashboard">
                <Button className="bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200]">
                  Dashboard
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                    <Avatar className="h-9 w-9">
                      {buyerProfile?.profile_image_url && (
                        <AvatarImage 
                          src={buyerProfile.profile_image_url} 
                          alt={buyerProfile?.full_name || "Buyer profile"} 
                        />
                      )}
                      <AvatarFallback className="bg-[#0052cc] text-white">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuItem asChild>
                    <Link to="/buyer/profile">My Profile</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/buyer/dashboard">Dashboard</Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-500">
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            // Show navigation when not logged in
            <>
              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-6">
                <Link to="/seller" className="text-gray-300 hover:text-[#ffd700] transition-colors">
                  For Sellers
                </Link>
                <Link to="/blog" className="text-gray-300 hover:text-[#ffd700] transition-colors">
                  Blog
                </Link>
                <Link to="/buyer/faq" className="text-gray-300 hover:text-[#ffd700] transition-colors">
                  FAQ
                </Link>
                <Link to="/contact" className="text-gray-300 hover:text-[#ffd700] transition-colors">
                  Contact
                </Link>
                <Link to="/buyer/login" className="text-gray-300 hover:text-[#ffd700] transition-colors">
                  Sign In
                </Link>
              </nav>

              {/* Mobile Dropdown Menu */}
              <div className="md:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200]">
                      <Menu className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    className="w-48 bg-[#080c14] border-gray-800 z-50"
                    align="end"
                  >
                    <DropdownMenuItem asChild>
                      <Link to="/buyer/register" className="text-[#ffd700] hover:text-[#ffd700] hover:bg-gray-800 cursor-pointer">
                        Create Free Account
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-gray-800" />
                    <DropdownMenuItem asChild>
                      <Link to="/buyer/login" className="text-gray-300 hover:text-[#ffd700] hover:bg-gray-800 cursor-pointer">
                        Sign In
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/seller" className="text-gray-300 hover:text-[#ffd700] hover:bg-gray-800 cursor-pointer">
                        For Sellers
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/blog" className="text-gray-300 hover:text-[#ffd700] hover:bg-gray-800 cursor-pointer">
                        Blog
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/buyer/faq" className="text-gray-300 hover:text-[#ffd700] hover:bg-gray-800 cursor-pointer">
                        FAQ
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/contact" className="text-gray-300 hover:text-[#ffd700] hover:bg-gray-800 cursor-pointer">
                        Contact
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Desktop CTA Button */}
              <Link to="/buyer/register" className="hidden md:block">
                <Button className="bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200]">
                  Create Free Account
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default BuyerLandingHeader;
